import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { publicProposalApi } from '@/api/commercial/public-proposal';
import { formatCents } from '@/lib/formatters';
import { billingSlipPhase, paymentPollingInterval } from './payment-polling';

export function AcceptancePaymentStep({ token }: { token: string }) {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pollingStartedAt, setPollingStartedAt] = useState(Date.now);
  const payment = useQuery({
    queryKey: ['public-payment', token],
    queryFn: () => publicProposalApi.findPayment(token),
    refetchInterval: (q) => {
      const state = q.state.data?.obligation.status;
      if (q.state.status === 'error') return false;
      return paymentPollingInterval(state, Date.now() - pollingStartedAt);
    },
  });
  const refresh = () => {
    setPollingStartedAt(Date.now());
    void payment.refetch();
  };
  const upload = useMutation({
    mutationFn: async (selected: File) => {
      const allowed = ['application/pdf', 'image/png', 'image/jpeg'];
      if (!allowed.includes(selected.type) || selected.size > 10 * 1024 * 1024)
        throw new Error('Envie PDF, PNG ou JPG de até 10 MB.');
      const signed = await publicProposalApi.createReceiptUpload(token, {
        originalName: selected.name,
        mimeType: selected.type,
      });
      const response = await fetch(signed.uploadUrl, {
        method: 'PUT',
        body: selected,
        headers: { 'Content-Type': selected.type },
      });
      if (!response.ok) throw new Error('Falha no envio do comprovante. Tente novamente.');
      // Fresh idempotency key per logical upload: a replacement is a NEW
      // operation and must never reuse the key of the receipt it replaces —
      // the backend rejects key reuse with different content.
      return publicProposalApi.confirmReceiptUpload(token, {
        storageKey: signed.storageKey,
        originalName: selected.name,
        idempotencyKey: crypto.randomUUID(),
      });
    },
    onSuccess: () => {
      setFile(null);
      queryClient.invalidateQueries({ queryKey: ['public-payment', token] });
    },
    onError: (e) =>
      setError(e instanceof Error ? e.message : 'Não foi possível enviar o comprovante.'),
  });
  if (payment.isLoading)
    return <div className="rounded-lg border p-5">Carregando dados de pagamento...</div>;
  if (payment.error || !payment.data)
    return (
      <div role="alert" className="rounded-lg border p-5 text-red-600">
        Pagamento indisponível. Tente novamente.
        <button type="button" disabled={payment.isFetching} onClick={refresh}>
          Tentar novamente
        </button>
      </div>
    );
  const { obligation, pix, billingSlip } = payment.data;
  const slipPhase = billingSlip ? billingSlipPhase[billingSlip.state] : undefined;
  const pollingExpired = Date.now() - pollingStartedAt >= 5 * 60_000;
  if (obligation.status === 'CANCELED')
    return <output className="block rounded border p-5">Pagamento cancelado.</output>;
  if (obligation.status === 'APPROVED')
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 p-5">Pagamento aprovado.</div>
    );
  return (
    <section className="space-y-4 rounded-lg border bg-white p-5">
      <h2 className="text-lg font-semibold">Aceite e Pagamento</h2>
      <p>
        Valor: <strong>{formatCents(obligation.amountInCents)}</strong>
      </p>
      {obligation.status === 'PENDING_VALIDATION' && (
        <output className="block">Comprovante recebido. Aguardando validação do pagamento.</output>
      )}
      {pollingExpired && (
        <button type="button" disabled={payment.isFetching} onClick={refresh}>
          Atualizar pagamento
        </button>
      )}
      {obligation.method === 'PIX' && pix && (
        <div className="space-y-2">
          <p>
            Chave Pix ({pix.keyType}): <strong>{pix.key}</strong>
          </p>
          <button
            type="button"
            className="text-sm font-medium text-blue-600 underline"
            onClick={() => navigator.clipboard.writeText(pix.key)}
          >
            Copiar chave Pix
          </button>
          <p>Favorecido: {pix.beneficiaryName}</p>
          <input
            type="file"
            accept=".pdf,.png,.jpg,.jpeg"
            onChange={(e) => {
              setError(null);
              setFile(e.target.files?.[0] ?? null);
            }}
          />
          <button
            type="button"
            disabled={!file || upload.isPending}
            onClick={() => file && upload.mutate(file)}
            className="rounded bg-blue-600 px-4 py-2 text-white disabled:opacity-50"
          >
            {upload.isPending ? 'Enviando...' : 'Enviar comprovante'}
          </button>
        </div>
      )}
      {obligation.method === 'BOLETO' && (
        <div>
          {slipPhase === 'success' && billingSlip?.data?.link ? (
            <a
              className="text-blue-600 underline"
              href={billingSlip.data.link}
              target="_blank"
              rel="noreferrer"
            >
              Abrir boleto
            </a>
          ) : slipPhase === 'error' ? (
            <p role="alert">O boleto precisa de revisão. Entre em contato com o atendimento.</p>
          ) : pollingExpired ? (
            <p>A geração ainda não foi concluída. Consulte novamente em alguns instantes.</p>
          ) : (
            <p>
              {billingSlip?.state === 'CANCEL_REQUESTED'
                ? 'Cancelamento do boleto em processamento.'
                : 'Seu boleto está sendo processado.'}
            </p>
          )}
          {!pollingExpired && slipPhase === 'error' && (
            <button type="button" disabled={payment.isFetching} onClick={refresh}>
              Atualizar pagamento
            </button>
          )}
        </div>
      )}
      {error && (
        <p role="alert" className="text-red-600">
          {error}
        </p>
      )}
    </section>
  );
}
